import React from 'react';
import { Typography, Box, List, ListItem, ListItemText, Chip } from '@mui/material';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import MetricCard from './MetricCard';
import ChartHeader from './ChartHeader';
import ChartCardWrapper from './ChartCardWrapper';

interface AlertViewProps {
    title: string;
    description: string;
    height?: number;
    graphHeight?: number;
    details?: any;
    onDetailsClick?: (data?: any) => void;
    gridExist?: boolean;
}

const AlertView: React.FC<AlertViewProps> = ({
    title,
    description,
    height = 310,
    graphHeight = 350,
    details,
    onDetailsClick,
    gridExist = false
}) => {
    const criticalCount = details?.criticalAlerts || 0;
    const highCount = details?.highAlerts || 0;
    const mediumCount = details?.mediumAlerts || 0;
    const alertsList = details?.alertsList || [];

    const handleCardClick = (data: any) => {
        if (onDetailsClick) {
            onDetailsClick(data);
        }
    };

    return (
        <ChartCardWrapper sx={{ height: height }}>
            <ChartHeader title={title} description={description} details={details} />
            {details?.label && (
                <Typography variant="body2" color="textSecondary" gutterBottom sx={{ textAlign: 'center' }}>
                    {details.label || ''}
                </Typography>
            )}
            {details?.subLabel && (
                <Typography variant="body2" color="textSecondary" gutterBottom sx={{ textAlign: 'center' }}>
                    {details.subLabel || ''}
                </Typography>
            )}
            <Box sx={{ mb: 2, marginTop: 3 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                    <MetricCard
                        data={{
                            title: details?.cardsLabels ? details.cardsLabels[0] : 'Critical',
                            value: criticalCount,
                            changeType: criticalCount > 0 ? 'negative' : 'neutral',
                            gradient: 'linear-gradient(135deg, #ef4444 0%, #f43f5e 100%)',
                            accentColor: '#ef4444',
                            icon: <WarningAmberIcon />,
                            onClick: () => handleCardClick({ severity: 'critical', count: criticalCount })
                        }}
                        compact
                        height={130}
                        gridExist={gridExist}
                    />
                    <MetricCard
                        data={{
                            title: details?.cardsLabels ? details.cardsLabels[1] : 'High',
                            value: highCount,
                            change: undefined,
                            changeType: highCount > 0 ? 'negative' : 'neutral',
                            gradient: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
                            accentColor: '#f59e0b',
                            icon: <ReportProblemIcon />,
                            onClick: () => handleCardClick({ severity: 'high', count: highCount })
                        }}
                        compact
                        height={130}
                        gridExist={gridExist}
                    />
                    <MetricCard
                        data={{
                            title: details?.cardsLabels ? details.cardsLabels[2] : 'Medium',
                            value: mediumCount,
                            change: undefined,
                            changeType: mediumCount > 0 ? 'neutral' : 'neutral',
                            gradient: 'linear-gradient(135deg, #fbbf24 0%, #fde68a 100%)',
                            accentColor: '#f59e0b',
                            icon: <InfoOutlined />,
                            onClick: () => handleCardClick({ severity: 'medium', count: mediumCount })
                        }}
                        compact
                        height={130}
                        gridExist={gridExist}
                    />
                </Box>
                <Typography variant="subtitle1" sx={{ mb: 1, marginTop: 2 }}>{details?.listLabel}</Typography>
                <Box sx={{ mt: 2, maxHeight: graphHeight, overflowY: 'auto' }}>
                    {Array.isArray(alertsList) && alertsList.length > 0 ? (
                        <List dense>
                            {alertsList.map((a: any, i: number) => (
                                <ListItem key={i} alignItems="flex-start" sx={{ py: 1 }}>
                                    <ListItemText
                                        primary={a.title ?? a.alert_title ?? `Alert ${i + 1}`}
                                        secondary={a.detail ?? a.description ?? a.alert_detail ?? ''}
                                    />
                                    {a.severity && (
                                        <Chip label={a.severity} size="small" color={a.severity === 'critical' ? 'error' : a.severity === 'high' ? 'warning' : 'default'} />
                                    )}
                                </ListItem>
                            ))}
                        </List>
                    ) : (
                        <Typography variant="body2" color="textSecondary">No alerts</Typography>
                    )}
                </Box>
            </Box>
            {details?.bottomDescription && (
                <Typography variant="body2" color="textSecondary" gutterBottom sx={{ textAlign: 'center', marginTop: 1 }}>
                    {details.bottomDescription || ''}
                </Typography>
            )}
        </ChartCardWrapper>
    );
};

export default AlertView;
